import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  FileDownload as FileDownloadIcon,
  UploadFile as UploadFileIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import { authGet, authPut } from '../../services/apiService';

const darkGreenColor = '#1D4434';

/** Split one CSV line, respecting double-quoted fields. */
const parseCsvLine = (line) => {
  const result = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i];
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (c === ',' && !inQuotes) {
      result.push(cur.trim());
      cur = '';
    } else {
      cur += c;
    }
  }
  result.push(cur.trim());
  return result;
};

const normalizeHeader = (h) => String(h || '').trim().toLowerCase().replace(/[\s_-]+/g, '');

const HEADER_RANK = new Set(['rank', 'serial', 'serialnumber', 'serialno', 'sno', '#', 'no', 'number']);
const HEADER_NAME = new Set(['name', 'accountname', 'customer', 'customername', 'firm', 'account', 'ownername', 'caseowner']);
const HEADER_ID = new Set([
  'accountid', 'salesforceaccountid', 'sfaccountid', 'salesforceid'
]);

/**
 * Parse CSV text into { rank, name, [idField], ...extraFields } rows.
 * Expects a header row with Rank/Serial, Name, and ACCOUNT_ID (flexible labels).
 * When requireRank is true, every data row must include an explicit Rank (no auto-fill).
 * `extraFields` columns are optional -- matched by key or label (e.g. "Instance_Name"
 * matches key "instanceName" or label "Instance Name"); when a column isn't found at all,
 * every row's value for it is left blank and its label is reported in `missingExtraFields`
 * so the caller can warn before Save silently clears any existing values for that field.
 * @returns {{ rows: Array, skippedBlankNames: number, missingExtraFields: string[] }}
 */
const parseRosterCsv = (text, idField, { requireRank = false, extraFields = [], idFieldLabel = '' } = {}) => {
  const lines = String(text || '')
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) {
    throw new Error('CSV needs a header row and at least one data row.');
  }

  const headers = parseCsvLine(lines[0]).map(normalizeHeader);
  let rankIdx = headers.findIndex((h) => HEADER_RANK.has(h));
  let nameIdx = headers.findIndex((h) => HEADER_NAME.has(h));
  let idIdx = headers.findIndex((h) => HEADER_ID.has(h) || h === normalizeHeader(idField) || (idFieldLabel && h === normalizeHeader(idFieldLabel)));
  const extraIdx = extraFields.map((f) => ({
    key: f.key,
    idx: headers.findIndex((h) => h === normalizeHeader(f.key) || h === normalizeHeader(f.label))
  }));

  // Fallback: Rank, Name, ID in that order when headers are missing/unrecognised.
  if (nameIdx < 0 && headers.length >= 2) {
    if (headers.length >= 3) {
      rankIdx = rankIdx < 0 ? 0 : rankIdx;
      nameIdx = 1;
      idIdx = idIdx < 0 ? 2 : idIdx;
    } else {
      nameIdx = 0;
      idIdx = 1;
    }
  }
  if (nameIdx < 0) {
    throw new Error('CSV must include a Name column (e.g. Rank,Name,ACCOUNT_ID).');
  }
  if (requireRank && rankIdx < 0) {
    throw new Error('CSV must include a Rank column (e.g. Rank,Name,ACCOUNT_ID).');
  }

  const rows = [];
  let skippedBlankNames = 0;
  const missingRankNames = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cols = parseCsvLine(lines[i]);
    const name = String(cols[nameIdx] || '').trim();
    if (!name) {
      skippedBlankNames += 1;
      continue;
    }
    const idVal = idIdx >= 0 ? String(cols[idIdx] || '').trim() : '';
    let rank = null;
    if (rankIdx >= 0 && cols[rankIdx] !== undefined && String(cols[rankIdx]).trim() !== '') {
      const n = Number(String(cols[rankIdx]).replace(/[^\d.-]/g, ''));
      if (Number.isFinite(n) && n >= 1) rank = Math.floor(n);
    }
    if (requireRank && rank == null) {
      missingRankNames.push(name);
      continue;
    }
    if (rank == null) rank = rows.length + 1;
    const extraVals = {};
    extraIdx.forEach(({ key, idx }) => {
      extraVals[key] = idx >= 0 ? String(cols[idx] || '').trim() : '';
    });
    rows.push({ rank, name, [idField]: idVal, ...extraVals });
  }

  if (requireRank && missingRankNames.length) {
    throw new Error(
      `Every CSV row needs a Rank. Missing for: ${missingRankNames.slice(0, 5).join(', ')}${missingRankNames.length > 5 ? '…' : ''}`
    );
  }
  if (!rows.length) {
    throw new Error('No account rows found in CSV.');
  }
  const missingExtraFields = extraFields
    .filter((f, i) => extraIdx[i].idx < 0)
    .map((f) => f.label);
  return { rows, skippedBlankNames, missingExtraFields };
};

const validateRanks = (rows) => {
  const withName = rows.filter((r) => String(r.name || '').trim());
  if (!withName.length) return null;
  const missing = withName.filter((r) => {
    const n = Number(r.rank);
    return !Number.isFinite(n) || n < 1;
  });
  if (missing.length) {
    return `Every account needs a Rank (positive whole number). Missing for: ${missing.map((r) => r.name).slice(0, 5).join(', ')}${missing.length > 5 ? '…' : ''}`;
  }
  const seen = new Map();
  for (const r of withName) {
    const rank = Math.floor(Number(r.rank));
    if (seen.has(rank)) {
      return `Duplicate Rank ${rank}: "${seen.get(rank)}" and "${r.name}". Each rank must be unique.`;
    }
    seen.set(rank, r.name);
  }
  return null;
};

const nextSuggestedRank = (rows) => {
  let max = 0;
  rows.forEach((r) => {
    const n = Number(r.rank);
    if (Number.isFinite(n) && n > max) max = n;
  });
  return max + 1;
};

const toCsvValue = (v) => {
  const s = String(v ?? '');
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

/**
 * Generic "name + Salesforce ID" roster editor -- shared by L1 Case Owners
 * (name + OWNER_ID) and Top Customer Accounts (name + ACCOUNT_ID). Same
 * load/edit/add/remove/save shape for both; each caller supplies its own
 * endpoint, labels, and any extra per-screen bits (a static info row, extra
 * actions like a sync button, via `staticRow` / `renderExtraActions` / `footer`).
 *
 * Optional `enableRank` adds an editable Rank column + duplicate-rank check.
 * Optional `enableCsv` adds Import / Export CSV (Rank, Name, ID, plus any `extraFields`).
 * Optional `extraFields` adds arbitrary free-text columns (e.g. Instance Name),
 * included in load/save/add-row/CSV import/CSV export alike.
 */
const NameIdRosterEditor = ({
  icon: Icon,
  title,
  description,
  infoAlert,
  listKey,
  idField,
  idFieldLabel,
  idPlaceholder,
  namePlaceholder = 'Name',
  loadUrl,
  saveUrl,
  addButtonLabel,
  emptyStateText,
  showRowNumber = false,
  extraFields = [],
  enableRank = false,
  enableCsv = false,
  csvFileName = 'roster.csv',
  staticRow = null,
  extraDisabled = false,
  buildSuccessMessage = (message) => message,
  renderExtraActions = null,
  footer = null
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [rows, setRows] = useState([]);
  const fileInputRef = useRef(null);

  const applyResponse = (data) => {
    const list = Array.isArray(data[listKey])
      ? data[listKey].map((r, index) => {
          const row = {
            name: r.name || '',
            [idField]: r[idField] || '',
            ...extraFields.reduce((acc, f) => ({ ...acc, [f.key]: r[f.key] || '' }), {})
          };
          if (enableRank) {
            const n = Number(r.rank);
            row.rank = Number.isFinite(n) && n >= 1 ? Math.floor(n) : index + 1;
          }
          return row;
        })
      : [];
    if (enableRank) {
      list.sort((a, b) => Number(a.rank) - Number(b.rank));
    }
    setRows(list);
  };

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await authGet(loadUrl, null, 'json', { useCache: false });
      if (response.success) {
        applyResponse(response);
      } else {
        setError(response.message || `Failed to load ${title}`);
      }
    } catch (err) {
      console.error(`Error loading ${title}:`, err);
      setError(err.response?.data?.message || err.message || `Failed to load ${title}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFieldChange = (index, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, [field]: value } : r)));
  };

  const handleAddRow = () => {
    setRows((prev) => [
      ...prev,
      {
        name: '',
        [idField]: '',
        ...extraFields.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {}),
        ...(enableRank ? { rank: nextSuggestedRank(prev) } : {})
      }
    ]);
  };

  const handleRemoveRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      if (enableRank) {
        const rankError = validateRanks(rows);
        if (rankError) {
          setError(rankError);
          setSaving(false);
          return;
        }
      }

      const payload = rows
        .map((r) => {
          const entry = {
            name: String(r.name || '').trim(),
            [idField]: String(r[idField] || '').trim(),
            ...extraFields.reduce((acc, f) => ({ ...acc, [f.key]: (r[f.key] || '').trim() }), {})
          };
          if (enableRank) {
            entry.rank = Math.floor(Number(r.rank));
          }
          return entry;
        })
        .filter((r) => r.name);

      const response = await authPut(saveUrl, { [listKey]: payload });
      if (response.success) {
        applyResponse(response);
        setSuccess(buildSuccessMessage(response.message || 'Saved'));
        setTimeout(() => setSuccess(null), 10000);
      } else {
        setError(response.message || 'Failed to save');
      }
    } catch (err) {
      console.error(`Error saving ${title}:`, err);
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleExportCsv = () => {
    const header = [
      ...(enableRank ? ['Rank'] : []),
      'Name',
      idFieldLabel,
      ...extraFields.map((f) => f.label)
    ].join(',');
    const body = rows
      .filter((r) => String(r.name || '').trim())
      .map((r) => {
        const cells = [
          ...(enableRank ? [r.rank] : []),
          r.name,
          r[idField],
          ...extraFields.map((f) => r[f.key] || '')
        ];
        return cells.map(toCsvValue).join(',');
      });
    const blob = new Blob([[header, ...body].join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = csvFileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImportCsv = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (event.target) event.target.value = '';
    if (!file) return;
    try {
      setError(null);
      setSuccess(null);
      const text = await file.text();
      const { rows: imported, skippedBlankNames, missingExtraFields } = parseRosterCsv(text, idField, {
        requireRank: enableRank,
        extraFields,
        idFieldLabel
      });
      if (enableRank) {
        const rankError = validateRanks(imported);
        if (rankError) {
          setError(rankError);
          return;
        }
        imported.sort((a, b) => Number(a.rank) - Number(b.rank));
      }
      setRows(imported);
      const skipNote = skippedBlankNames
        ? ` (skipped ${skippedBlankNames} blank-name row${skippedBlankNames === 1 ? '' : 's'})`
        : '';
      if (missingExtraFields && missingExtraFields.length) {
        // Every row's value for these fields imported blank -- warn before Save
        // silently clears any existing values, instead of leaving this silent.
        setError(
          `No "${missingExtraFields.join('", "')}" column found in this CSV -- ${missingExtraFields.length === 1 ? 'that field' : 'those fields'} will be cleared for every row if you Save. Add the column and re-import, or remove affected rows before saving.`
        );
      }
      setSuccess(
        `Loaded ${imported.length} row${imported.length === 1 ? '' : 's'} from CSV${skipNote}. Review ranks, then Save.`
      );
      if (!missingExtraFields || !missingExtraFields.length) {
        setTimeout(() => setSuccess(null), 10000);
      }
    } catch (err) {
      setError(err.message || 'Failed to parse CSV');
    }
  };

  const disabled = loading || saving || extraDisabled;
  const showHash = showRowNumber && !enableRank;
  const columnCount = (enableRank || showHash ? 1 : 0) + 3 + extraFields.length;

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ color: darkGreenColor, mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          {Icon && <Icon />} {title}
        </Typography>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: infoAlert ? 1 : 2 }}>
            {description}
          </Typography>
        )}
        {infoAlert && <Alert severity="info" sx={{ mb: 2 }}>{infoAlert}</Alert>}

        {loading && <LinearProgress sx={{ mb: 2 }} />}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <TableContainer sx={{ mb: 2, maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {enableRank && <TableCell sx={{ width: 88 }}>Rank</TableCell>}
                {showHash && <TableCell sx={{ width: 56 }}>#</TableCell>}
                <TableCell>Name</TableCell>
                <TableCell>{idFieldLabel}</TableCell>
                {extraFields.map((f) => (
                  <TableCell key={f.key}>{f.label}</TableCell>
                ))}
                <TableCell sx={{ width: 56 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {staticRow}
              {rows.map((row, index) => (
                <TableRow key={`row-${index}`}>
                  {enableRank && (
                    <TableCell>
                      <TextField
                        value={row.rank ?? ''}
                        onChange={(e) => handleFieldChange(index, 'rank', e.target.value)}
                        size="small"
                        type="number"
                        inputProps={{ min: 1, step: 1, style: { width: 56 } }}
                        placeholder="1"
                        error={(() => {
                          const n = Number(row.rank);
                          if (!Number.isFinite(n) || n < 1) return Boolean(String(row.name || '').trim());
                          return rows.some(
                            (other, j) =>
                              j !== index &&
                              String(other.name || '').trim() &&
                              Math.floor(Number(other.rank)) === Math.floor(n)
                          );
                        })()}
                      />
                    </TableCell>
                  )}
                  {showHash && (
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{index + 1}</Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <TextField
                      value={row.name}
                      onChange={(e) => handleFieldChange(index, 'name', e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={namePlaceholder}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={row[idField]}
                      onChange={(e) => handleFieldChange(index, idField, e.target.value)}
                      size="small"
                      fullWidth
                      placeholder={idPlaceholder}
                    />
                  </TableCell>
                  {extraFields.map((f) => (
                    <TableCell key={f.key}>
                      <TextField
                        value={row[f.key] || ''}
                        onChange={(e) => handleFieldChange(index, f.key, e.target.value)}
                        size="small"
                        fullWidth
                        placeholder={f.placeholder}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    <IconButton size="small" onClick={() => handleRemoveRow(index)} aria-label="Remove">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!rows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={columnCount}>
                    <Typography variant="body2" color="text.secondary">{emptyStateText}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button startIcon={<AddIcon />} onClick={handleAddRow} disabled={disabled}>
            {addButtonLabel}
          </Button>
          {enableCsv && (
            <>
              <Button
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                disabled={disabled}
              >
                Import CSV
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={handleImportCsv}
              />
              <Button
                startIcon={<FileDownloadIcon />}
                onClick={handleExportCsv}
                disabled={disabled || !rows.some((r) => String(r.name || '').trim())}
              >
                Export CSV
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={disabled}
            sx={{ bgcolor: darkGreenColor, '&:hover': { bgcolor: '#163528' } }}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {renderExtraActions && renderExtraActions({ disabled })}
        </Box>
        {footer}
      </Paper>
    </Box>
  );
};

export default NameIdRosterEditor;
